import type { CooldownRecord, ExpirationCondition, TimedEffectRecord } from '../domain/encounter.ts';
import type { RuntimeState } from '../domain/runtime.ts';

/**
 * IN-GAME elapsed time.
 *
 * This is the one place time passes. It is not a calendar and not a clock: it
 * has no date, no time of day and no relationship to the wall clock. It answers
 * exactly one question - how much game time has gone by - because two things in
 * the rules are measured that way and had no way to expire:
 *
 *  - Debuffs with a printed duration. Shit-Faced lasts 10 minutes and Sore as
 *    Shit an hour (Rules p.45, Table 8). Nothing made those minutes pass, so
 *    they lasted until someone removed them by hand.
 *  - Cooldowns measured in hours. Powerful Strike is 30 hours (C&C p.22).
 *
 * One primitive serves both. A second clock for cooldowns would drift from the
 * one for Debuffs the first time either was advanced without the other.
 *
 * Everything that makes time pass calls `advanceTime`: a combat round, a rest,
 * and the Play Sheet's pass-time control.
 *
 * RULE DCC-TIME-001 - a round is 10 seconds (Rules p.17), so 60 rounds are 10
 * minutes. Rest durations are printed: Short 2 hours, Long 8, Full Day's 30
 * (Rules p.43).
 */

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
export const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

/** RULE DCC-ENC-001 - Rules p.17. A combat round lasts ten seconds. */
export const ROUND_SECONDS = 10;

/**
 * How long a cooldown lasts in seconds, or null when it is not measured in time.
 *
 * `round` and `scene` end with the encounter and `floor` when the crawler
 * changes floor; none of those is a duration, so none is advanced by the clock.
 *
 * `day` is read as 24 hours. The source writes "once per day" (Rules p.17)
 * without defining a day in a dungeon that has no daylight, and the only other
 * duration the rules give in days is the Full Day's Rest at 30 hours - which is
 * a REST, not a day. Twenty-four hours is the plain reading. Recorded in
 * docs/RULES_CONFLICTS.md; if the table rules otherwise, change it here alone.
 */
export function cooldownDurationSeconds(record: CooldownRecord): number | null {
  switch (record.scope) {
    case 'hours':
      return record.amount === undefined ? null : record.amount * SECONDS_PER_HOUR;
    case 'days':
      return record.amount === undefined ? null : record.amount * SECONDS_PER_DAY;
    case 'day':
      return SECONDS_PER_DAY;
    case 'round':
    case 'scene':
    case 'floor':
      return null;
  }
}

export interface TimeAdvanceResult {
  runtime: RuntimeState;
  /** Effects whose printed duration ran out. */
  expiredEffectIds: string[];
  /** Cooldowns that finished. */
  endedCooldownIds: string[];
}

/**
 * Advances in-game time and expires whatever ran out.
 *
 * Durations are stored as time REMAINING and counted down, rather than as an
 * expiry stamp against a running total. That keeps a saved crawler meaningful
 * on its own: "Shit-Faced, 4 minutes left" survives inspection of the JSON,
 * where "expires at second 91,400" would need the total to interpret.
 *
 * An Effect ends when ANY of its printed conditions is satisfied, so an Effect
 * whose time runs out is removed even if it also has a Check or a rest that
 * could have ended it. Whichever comes first wins (Rules p.44).
 *
 * Nothing here applies damage. Recurring damage is owed at Clean Up for the
 * round that is ending, and `advanceRound` collects it BEFORE calling this, so
 * a Debuff expiring on the same round still deals the damage it owes.
 */
export function advanceTime(runtime: RuntimeState, seconds: number): TimeAdvanceResult {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return { runtime, expiredEffectIds: [], endedCooldownIds: [] };
  }

  const expiredEffectIds: string[] = [];
  const active: TimedEffectRecord[] = [];

  for (const record of runtime.active) {
    let expired = false;
    const endsWhen: ExpirationCondition[] = [];

    for (const condition of record.endsWhen) {
      if (condition.kind !== 'elapsed-time') {
        endsWhen.push(condition);
        continue;
      }
      const remaining = condition.seconds - seconds;
      if (remaining <= 0) expired = true;
      else endsWhen.push({ kind: 'elapsed-time', seconds: remaining });
    }

    if (expired) expiredEffectIds.push(record.effect.id);
    else active.push({ ...record, endsWhen });
  }

  const endedCooldownIds: string[] = [];
  const cooldowns: CooldownRecord[] = [];

  for (const cooldown of runtime.cooldowns) {
    if (cooldown.secondsRemaining === undefined) {
      // Not measured in time: it ends with the encounter or the floor.
      cooldowns.push(cooldown);
      continue;
    }
    const remaining = cooldown.secondsRemaining - seconds;
    if (remaining <= 0) endedCooldownIds.push(cooldown.sourceId);
    else cooldowns.push({ ...cooldown, secondsRemaining: remaining });
  }

  return {
    runtime: {
      ...runtime,
      elapsedSeconds: runtime.elapsedSeconds + seconds,
      active,
      cooldowns,
    },
    expiredEffectIds,
    endedCooldownIds,
  };
}

/** Reads as "1h 05m", for the Play Sheet. */
export function describeElapsed(seconds: number): string {
  const hours = Math.floor(seconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const rest = seconds % SECONDS_PER_MINUTE;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

/**
 * How a cooldown reads in a list.
 *
 * A cooldown measured in time shows what is LEFT of it, because that is the
 * useful state - "22h remaining" tells the table something, "(hours)" does not.
 * Round-, scene- and floor-scoped cooldowns have no duration, so they show the
 * scope they end with instead.
 *
 * Lives here rather than in a component so the encounter panel and anything
 * later cannot format it two different ways.
 */
export function describeCooldown(record: CooldownRecord): string {
  if (record.secondsRemaining === undefined) {
    switch (record.scope) {
      case 'round':
        return 'until the end of this round';
      case 'scene':
        return 'until the encounter ends';
      case 'floor':
        return 'until you leave this floor';
      default:
        // A time-based scope with no duration recorded: say so rather than
        // implying a length that was never known.
        return `${record.scope} (no duration recorded)`;
    }
  }
  return `${describeElapsed(record.secondsRemaining)} remaining`;
}

/** The increments the Play Sheet offers for passing time outside a fight. */
export const TIME_STEPS: readonly { label: string; seconds: number }[] = [
  { label: '10 seconds', seconds: ROUND_SECONDS },
  { label: '1 minute', seconds: SECONDS_PER_MINUTE },
  { label: '10 minutes', seconds: 10 * SECONDS_PER_MINUTE },
  { label: '1 hour', seconds: SECONDS_PER_HOUR },
];
